import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { Terminal, Shield, Zap } from 'lucide-react';
import { validatePassword, validateEmail, validateUsername } from '../../utils/validation';
import { debounce } from '../../utils/debounce';
import { authAPI } from '../../services/api';
import { OAuthButtons } from './OAuthButtons';

interface SignupFormProps {
  onSuccess: () => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  
  const { register } = useAuth();

  // Debounced email check
  const checkEmailDebounced = useCallback(
    debounce(async (email: string) => {
      if (!email || !validateEmail(email)) return;
      
      setIsCheckingEmail(true);
      try {
        const exists = await authAPI.checkEmailExists(email);
        if (exists) {
          setEmailError('This email address is already registered');
        } else {
          setEmailError('');
        }
      } catch (err) {
        console.warn('Email check failed:', err);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500),
    []
  );

  // Check email when it changes
  useEffect(() => {
    if (email && validateEmail(email)) {
      checkEmailDebounced(email);
    } else {
      setEmailError('');
    }
  }, [email, checkEmailDebounced]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || isCheckingEmail) return;

    setLoading(true);
    setError('');
    setPasswordErrors([]);

    try {
      const trimmedUsername = username.trim();
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedPassword = password.trim();

      // Validate all fields
      if (!trimmedUsername || !trimmedEmail || !trimmedPassword) {
        throw new Error('Please fill in all fields');
      }

      // Validate email
      if (!validateEmail(trimmedEmail)) {
        throw new Error('Please enter a valid email address');
      }

      if (emailError) {
        throw new Error(emailError);
      }

      // Validate username
      if (!validateUsername(trimmedUsername)) {
        throw new Error('Username must be at least 3 characters and can only contain letters, numbers, underscores, and hyphens');
      }

      // Validate password
      const passwordValidation = validatePassword(trimmedPassword);
      if (!passwordValidation.isValid) {
        setPasswordErrors(passwordValidation.errors);
        throw new Error('Please fix the password requirements');
      }

      const success = await register(trimmedUsername, trimmedEmail, trimmedPassword);
      if (success) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      if (err.message) {
        setError(err.message);
      } else if (err.response?.data?.detail) {
        const errorDetail = err.response.data.detail.toLowerCase();
        if (errorDetail.includes('email')) {
          setError('This email address is already registered');
        } else if (errorDetail.includes('username')) {
          setError('This username is already taken');
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="card p-8 shadow-2xl border-gray-600">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Terminal className="w-12 h-12 text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Start Free Trial
            </h1>
            <p className="text-gray-400">
              14 days of unlimited access. No credit card required.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
              <Terminal className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Access all DevOps challenges</span>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-300">Real Linux environments</span>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
              <Zap className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-300">Detailed solutions & support</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input"
                placeholder="Choose a username"
                required
                minLength={3}
                maxLength={50}
                pattern="[a-zA-Z0-9_-]+"
                title="Username can only contain letters, numbers, underscores, and hyphens"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`input ${emailError ? 'border-red-500' : ''}`}
                placeholder="Enter your email"
                required
              />
              {isCheckingEmail && (
                <p className="text-xs text-blue-400 mt-1">
                  Checking email availability...
                </p>
              )}
              {emailError && (
                <p className="text-xs text-red-400 mt-1">
                  {emailError}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                We'll send your trial activation link here
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  const validation = validatePassword(e.target.value);
                  setPasswordErrors(validation.errors);
                }}
                className="input"
                placeholder="Create a strong password"
                required
              />
              {passwordErrors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {passwordErrors.map((error, index) => (
                    <p key={index} className="text-sm text-red-400">
                      • {error}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || isCheckingEmail}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing up...' : 'Start Free Trial'}
              </button>
            </div>

            {/* OAuth separator and buttons */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900 text-gray-400">Or continue with</span>
                </div>
              </div>
              <div className="mt-6">
                <OAuthButtons onError={setError} />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mt-4" role="alert">
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center">
              By signing up, you agree to our Terms of Service and Privacy Policy
            </p>
          </form>

          <div className="text-center mt-6">
            <a
              href="/login"
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              Already have an account? Sign in
            </a>
          </div>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-blue-300 text-sm text-center">
              💡 Start with our "Fix SSH Configuration" challenge after signing up!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 